import {
  showDesktopNotification,
  clearDesktopNotifications
} from 'util/desktopNotifications';
import api from 'util/api';
import { useCallback, useEffect, useRef } from 'react';
import {
  useGetNotificationInboxQuery,
  NOTIFICATION_INBOX_TAG
} from 'rtk/notificationInbox.slice';
import 'rtk/org-admin/organizationBilling.slice';
import { useDispatch, useSelector } from 'react-redux';
import { rtkAPI } from 'rtk/rtkSetup';
import { SHARE_REQUEST_TAGS } from 'rtk/org-admin/share-request.slice';
import { NOTARIES_TAGS } from 'rtk/org-admin/notaries.slice';
import { JOURNAL_REPO_TAGS } from 'rtk/org-admin/journalRepo.slice';
import { TENANT_DASHBOARD_TAGS } from 'rtk/org-admin/tenantDashboard.slice';
import { notificationSlice } from 'stores/notification';
import { refreshSubscriptionStatus } from 'stores/subscription';
import logger from 'util/logger';
import config from 'util/config';
import c from 'util/const';
import {
  inboxNotificationTarget,
  shareNotificationTarget
} from 'util/notificationRouting';
import { connectShareRequestNotifications } from 'util/shareRequestSignalR';

const eventTypes = new Set([
  'share_request_created',
  'share_request_updated',
  'association_removed'
]);

export default function useShareRequestNotifications(
  token,
  userGuid,
  navigateTo
) {
  const dispatch = useDispatch();
  const roles = useSelector(state => state.authentication.userRoles);
  const isAdmin = roles?.includes(c.roles.TENANT_ADMIN);
  const inbox = useGetNotificationInboxQuery(
    { userGuid, indexStart: 0, maxResults: 20 },
    {
      skip: !token || !userGuid,
      // SignalR pushes inbox changes; polling is only a slow safety net when it is configured.
      pollingInterval: config.signalRUrl ? 300000 : 30000,
      skipPollingIfUnfocused: true,
      refetchOnMountOrArgChange: true
    }
  );
  const knownInbox = useRef(null);
  const announced = useRef(new Set());
  const openDesktopTarget = useCallback(
    (target, guid) => {
      api.NotificationInbox.markRead(guid)
        .then(() => {
          dispatch(notificationSlice.actions.markNotificationRead(guid));
          dispatch(rtkAPI.util.invalidateTags([NOTIFICATION_INBOX_TAG]));
        })
        .catch(() =>
          dispatch(
            notificationSlice.actions.setInboxError(
              'Unable to mark the notification as read. Open Notifications to retry.'
            )
          )
        );
      if (navigateTo) navigateTo(target);
    },
    [dispatch, navigateTo]
  );

  useEffect(() => {
    knownInbox.current = null;
    announced.current.clear();
    clearDesktopNotifications();
    dispatch(notificationSlice.actions.clearInboxSession());
  }, [dispatch, token, userGuid]);
  useEffect(() => {
    if (!token || !userGuid || !inbox.currentData) return;
    const data = inbox.currentData?.Data ?? inbox.currentData;
    const page = {
      ...data,
      Objects: (data.Objects || []).map(item => ({
        ...item,
        ActionUrl: inboxNotificationTarget(item, isAdmin, userGuid)
      }))
    };
    const previous = knownInbox.current;
    const sponsorshipChanged =
      !isAdmin &&
      (page.Objects || []).some(
        item =>
          item.EventType?.startsWith('Sponsorship') &&
          (!previous || !previous.has(item.GUID))
      );
    if (sponsorshipChanged) {
      dispatch(refreshSubscriptionStatus()).catch(error =>
        logger.warn('Unable to refresh sponsored subscription status', error)
      );
    }
    (page.Objects || []).forEach(item => {
      if (
        previous &&
        !previous.has(item.GUID) &&
        item.Unread &&
        !announced.current.has(item.GUID)
      ) {
        announced.current.add(item.GUID);
        showDesktopNotification(item, openDesktopTarget);
      }
    });
    knownInbox.current = new Set((page.Objects || []).map(item => item.GUID));
    dispatch(notificationSlice.actions.replaceInboxPage({ userGuid, page }));
  }, [
    dispatch,
    inbox.currentData,
    token,
    userGuid,
    isAdmin,
    openDesktopTarget
  ]);
  useEffect(() => {
    if (token && userGuid && inbox.error)
      dispatch(
        notificationSlice.actions.setInboxError(
          'Unable to refresh notifications. Open the inbox to retry.'
        )
      );
  }, [dispatch, inbox.error, token, userGuid]);

  useEffect(() => () => clearDesktopNotifications(), [token, userGuid]);

  useEffect(() => {
    if (!token || !userGuid) {
      dispatch(notificationSlice.actions.clearInboxSession());
      return;
    }
    if (!config.signalRUrl) return;
    const refresh = () => {
      if (!isAdmin) {
        dispatch(refreshSubscriptionStatus()).catch(error =>
          logger.warn('Unable to refresh subscription status', error)
        );
      }
      dispatch(
        rtkAPI.util.invalidateTags([
          SHARE_REQUEST_TAGS.SHARE_REQUEST,
          NOTARIES_TAGS.REPOSITORY_USER,
          JOURNAL_REPO_TAGS.LIST,
          TENANT_DASHBOARD_TAGS.METRICS,
          NOTIFICATION_INBOX_TAG,
          'OrganizationBilling'
        ])
      );
    };
    const seen = new Set();
    return connectShareRequestNotifications({
      url: config.signalRUrl,
      token,
      onConnected: refresh,
      onInboxChanged: refresh,
      onEvent: event => {
        if (!eventTypes.has(event?.Type) || !event.ShareRequestGuid) return;
        const id =
          event.NotificationGuid ||
          `${event.ShareRequestGuid}:${event.Type}:${event.Action}:${event.TimestampUtc}`;
        if (seen.has(id)) return;
        seen.add(id);
        if (seen.size > 200) seen.delete(seen.values().next().value);
        dispatch(
          notificationSlice.actions.addShareRequestNotification({
            id,
            event,
            to: shareNotificationTarget(event, isAdmin, userGuid)
          })
        );
        if (event.Message && !announced.current.has(id)) {
          announced.current.add(id);
          showDesktopNotification(
            {
              GUID: id,
              Title: event.Title || 'Share request',
              Message: event.Message,
              ActionUrl: shareNotificationTarget(event, isAdmin, userGuid)
            },
            openDesktopTarget
          );
        }
        refresh();
      }
    });
  }, [dispatch, token, userGuid, isAdmin, navigateTo, openDesktopTarget]);
}
